/**
 * API Caching Utility
 * Caches API responses and thumbnails to reduce redundant requests
 * Uses localStorage for persistence across sessions
 */

const CACHE_PREFIX = 'ruch_cache_';
const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes
const MAX_CACHE_SIZE = 50; // Maximum number of cached items

class APICache {
  constructor() {
    this.memoryCache = new Map();
    this.cleanupOldCache();
  }

  /**
   * Generate cache key from URL
   * Use full base64 or hash to ensure uniqueness
   */
  getCacheKey(url) {
    // Use simple hash for shorter, unique keys
    let hash = 0;
    for (let i = 0; i < url.length; i++) {
      const char = url.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return `${CACHE_PREFIX}${Math.abs(hash).toString(36)}`;
  }

  /**
   * Get data from cache (memory first, then localStorage)
   */
  get(url) {
    const key = this.getCacheKey(url);

    // Check memory cache first (faster)
    if (this.memoryCache.has(key)) {
      const cached = this.memoryCache.get(key);
      if (this.isValid(cached)) {
        console.log('✅ Cache HIT (memory):', url.substring(0, 50));
        return cached.data;
      }
      this.memoryCache.delete(key);
    }

    // Check localStorage
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (this.isValid(parsed)) {
          console.log('✅ Cache HIT (storage):', url.substring(0, 50));
          // Promote to memory cache
          this.memoryCache.set(key, parsed);
          return parsed.data;
        }
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.error('Cache read error:', error);
    }

    console.log('❌ Cache MISS:', url.substring(0, 50));
    return null;
  }

  /**
   * Store data in cache
   */
  set(url, data) {
    const key = this.getCacheKey(url);
    const cached = {
      data,
      timestamp: Date.now(),
      url: url.substring(0, 100) // Store partial URL for debugging
    };

    // Store in memory cache
    this.memoryCache.set(key, cached);

    // Store in localStorage
    try {
      // Limit cache size
      this.enforceMaxSize();
      localStorage.setItem(key, JSON.stringify(cached));
      console.log('💾 Cached:', url.substring(0, 50));
    } catch (error) {
      // localStorage full - clear old items
      console.warn('Cache storage full, clearing old items');
      this.clearOldest();
      try {
        localStorage.setItem(key, JSON.stringify(cached));
      } catch (e) {
        console.error('Failed to cache:', e);
      }
    }
  }

  /**
   * Check if cached data is still valid
   */
  isValid(cached) {
    if (!cached || !cached.timestamp) return false;
    const age = Date.now() - cached.timestamp;
    return age < CACHE_EXPIRY;
  }

  /**
   * Cleanup old cache entries on startup
   */
  cleanupOldCache() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          try {
            const cached = JSON.parse(localStorage.getItem(key));
            if (!this.isValid(cached)) {
              localStorage.removeItem(key);
            }
          } catch (e) {
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('Cache cleanup error:', error);
    }
  }

  /**
   * Enforce maximum cache size
   */
  enforceMaxSize() {
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
      if (keys.length >= MAX_CACHE_SIZE) {
        this.clearOldest();
      }
    } catch (error) {
      console.error('Enforce max size error:', error);
    }
  }

  /**
   * Clear oldest cache entries
   */
  clearOldest() {
    try {
      const entries = Object.keys(localStorage)
        .filter(k => k.startsWith(CACHE_PREFIX))
        .map(key => {
          try {
            const cached = JSON.parse(localStorage.getItem(key));
            return { key, timestamp: cached.timestamp || 0 };
          } catch (e) {
            return { key, timestamp: 0 };
          }
        })
        .sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest 20%
      const toRemove = Math.ceil(entries.length * 0.2);
      for (let i = 0; i < toRemove && i < entries.length; i++) {
        localStorage.removeItem(entries[i].key);
        const memKey = entries[i].key;
        this.memoryCache.delete(memKey);
      }
    } catch (error) {
      console.error('Clear oldest error:', error);
    }
  }

  /**
   * Clear all cache
   */
  clear() {
    this.memoryCache.clear();
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Cache clear error:', error);
    }
  }

  /**
   * Get cache stats
   */
  getStats() {
    const memorySize = this.memoryCache.size;
    let storageSize = 0;
    try {
      storageSize = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX)).length;
    } catch (e) {
      // ignore
    }
    return {
      memorySize,
      storageSize,
      totalSize: memorySize + storageSize
    };
  }
}

// Singleton instance
const apiCache = new APICache();

/**
 * Cached fetch wrapper
 */
export const cachedFetch = async (url, options = {}) => {
  // Check cache first
  const cached = apiCache.get(url);
  if (cached) {
    return cached;
  }

  // Fetch from API
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const data = await response.json();

  // Cache the response
  apiCache.set(url, data);

  return data;
};

/**
 * Preload and cache images
 */
export const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No URL provided'));
      return;
    }

    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Preload multiple images
 */
export const preloadImages = async (urls) => {
  const validUrls = urls.filter(Boolean);
  const results = await Promise.allSettled(
    validUrls.map(url => preloadImage(url))
  );

  const loaded = results.filter(r => r.status === 'fulfilled').length;
  console.log(`📸 Preloaded ${loaded}/${validUrls.length} thumbnails`);

  return results;
};

/**
 * Extract thumbnail URLs from movie data
 */
export const extractThumbnails = (movies) => {
  const urls = [];

  movies.forEach(movie => {
    const thumbnails = movie?.snippet?.thumbnails || movie?.thumbnails;
    if (thumbnails) {
      // Get all available quality thumbnails
      if (thumbnails.maxres?.url) urls.push(thumbnails.maxres.url);
      if (thumbnails.standard?.url) urls.push(thumbnails.standard.url);
      if (thumbnails.high?.url) urls.push(thumbnails.high.url);
      if (thumbnails.medium?.url) urls.push(thumbnails.medium.url);
    }
  });

  return [...new Set(urls)]; // Remove duplicates
};

// Export cache instance for debugging
export { apiCache };

export default cachedFetch;
